import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { defaultDisplayPreferences, type DisplayPreferences } from '@email/shared';
import { toast } from 'sonner';
import {
  saveDisplayPreferences,
  useDisplayPreferences,
} from '../../api/queries.js';
import { settingsKeys } from '../../api/query-cache.js';
import { currentDisplayPreferences, useUiStore } from '../../stores/ui.js';

const SAVE_DELAY_MS = 400;
const RETRY_DELAY_MS = 5_000;
const LOCAL_OWNER_KEY = 'ui-preferences-user';

/**
 * Reconciles the persisted local UI store with the authenticated user's
 * server preference document. Local state stays the instant/offline cache;
 * SSE-invalidated query results move changes between browsers.
 */
export function usePreferenceSync(userId: number | null): void {
  const enabled = userId !== null;
  const queryClient = useQueryClient();
  const query = useDisplayPreferences(userId);
  const generation = useRef(0);
  const hydrated = useRef(false);
  const writing = useRef(false);
  const queued = useRef<DisplayPreferences | null>(null);
  const lastSyncedJson = useRef('');
  const lastUpdatedAt = useRef(0);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = (timer: typeof saveTimer) => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };

  const flushQueue = useCallback(async () => {
    if (!enabled || writing.current) return;
    const runGeneration = generation.current;
    writing.current = true;
    while (queued.current) {
      const preferences = queued.current;
      queued.current = null;
      try {
        const response = await saveDisplayPreferences(preferences);
        if (runGeneration !== generation.current) return;
        const responseJson = serialize(response.preferences);
        lastSyncedJson.current = responseJson;
        lastUpdatedAt.current = response.updatedAt;
        if (userId !== null) {
          queryClient.setQueryData(settingsKeys.preference(userId), response);
        }
        // The server clears defaults that no longer belong to this user or are
        // unavailable. Reflect that normalization locally without echoing it.
        if (responseJson !== serialize(preferences)) {
          useUiStore.getState().applyDisplayPreferences(response.preferences);
        }
      } catch {
        if (runGeneration !== generation.current) return;
        if (queued.current === null) queued.current = preferences;
        toast.error('Display preferences could not sync; Wintermute will retry.', {
          id: 'preference-sync-error',
        });
        clearTimer(retryTimer);
        retryTimer.current = setTimeout(() => void flushQueue(), RETRY_DELAY_MS);
        break;
      }
    }
    writing.current = false;
  }, [enabled, queryClient, userId]);

  const queueSave = useCallback(
    (preferences: DisplayPreferences, immediate = false) => {
      queued.current = preferences;
      clearTimer(saveTimer);
      clearTimer(retryTimer);
      if (immediate) void flushQueue();
      else saveTimer.current = setTimeout(() => void flushQueue(), SAVE_DELAY_MS);
    },
    [flushQueue],
  );

  useEffect(() => {
    generation.current += 1;
    hydrated.current = false;
    writing.current = false;
    queued.current = null;
    lastSyncedJson.current = '';
    lastUpdatedAt.current = 0;
    clearTimer(saveTimer);
    clearTimer(retryTimer);
  }, [userId]);

  useEffect(() => {
    if (!enabled) {
      hydrated.current = false;
      queued.current = null;
      lastSyncedJson.current = '';
      lastUpdatedAt.current = 0;
      clearTimer(saveTimer);
      clearTimer(retryTimer);
      return;
    }
    const remote = query.data;
    if (!remote) return;
    const remoteJson = serialize(remote.preferences);

    if (!hydrated.current) {
      const localOwner = readLocalOwner();
      const local =
        remote.updatedAt === 0 && localOwner !== null && localOwner !== userId
          ? defaultDisplayPreferences()
          : currentDisplayPreferences();
      hydrated.current = true;
      lastSyncedJson.current = remoteJson;
      lastUpdatedAt.current = remote.updatedAt;
      writeLocalOwner(userId);
      if (remote.updatedAt === 0) {
        if (serialize(local) !== serialize(currentDisplayPreferences())) {
          useUiStore.getState().applyDisplayPreferences(local);
        }
        queueSave(local, true);
      } else if (serialize(local) !== remoteJson) {
        useUiStore.getState().applyDisplayPreferences(remote.preferences);
      }
      return;
    }

    if (remote.updatedAt < lastUpdatedAt.current) return;
    lastUpdatedAt.current = remote.updatedAt;
    if (remoteJson === lastSyncedJson.current) return;

    // A newer document arrived from another browser. Cancel an unsent local
    // debounce and apply the latest committed server version without echoing.
    clearTimer(saveTimer);
    clearTimer(retryTimer);
    queued.current = null;
    lastSyncedJson.current = remoteJson;
    useUiStore.getState().applyDisplayPreferences(remote.preferences);
  }, [enabled, query.data, queueSave, userId]);

  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = useUiStore.subscribe(() => {
      if (!hydrated.current) return;
      const preferences = currentDisplayPreferences();
      if (serialize(preferences) !== lastSyncedJson.current) queueSave(preferences);
    });
    return () => {
      unsubscribe();
      clearTimer(saveTimer);
      clearTimer(retryTimer);
    };
  }, [enabled, queueSave]);
}

function serialize(preferences: DisplayPreferences): string {
  return JSON.stringify(preferences);
}

function readLocalOwner(): number | null {
  try {
    const value = Number(localStorage.getItem(LOCAL_OWNER_KEY));
    return Number.isInteger(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeLocalOwner(userId: number): void {
  try {
    localStorage.setItem(LOCAL_OWNER_KEY, String(userId));
  } catch {
    // Zustand's local cache may also be unavailable; server sync still works.
  }
}
